import { useMemo, useState } from 'react'

import { useLaborRates, useSetLaborRates } from '../../lib/admin'
import type { LaborRate } from '../../lib/admintypes'

import { AdminLayout } from './AdminLayout'

/**
 * Labour rates (02 §0.2, WS2-14) — the other half of every price the studio shows.
 *
 * A rate that has not been set is **null**, and null renders as "Estimated on site" everywhere
 * it appears. That is not a placeholder waiting to be filled with a guess: 01 §7 forbids the
 * studio from stating a figure Andrew has not stood behind, so an unset rate is the correct
 * behaviour, not a gap.
 *
 * The screen is sorted by **how many subservices use each key**, because eighty keys is an
 * afternoon and the first ten are most of the value. Saving writes a new version rather than
 * editing in place — a packet emailed last week quoted a number and that has to stay true.
 */

const UNIT_LABEL: Record<string, string> = {
  each: 'each',
  sqft: 'per sq ft',
  lnft: 'per linear ft',
  hour: 'per hour',
}

export function AdminLabor() {
  const rates = useLaborRates()
  const save = useSetLaborRates()
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [onlyUnset, setOnlyUnset] = useState(false)
  const [saved, setSaved] = useState<number | null>(null)

  const rows = useMemo(() => {
    const all = rates.data ?? []
    return onlyUnset ? all.filter((rate) => rate.from_price === null) : all
  }, [rates.data, onlyUnset])

  const unset = (rates.data ?? []).filter((rate) => rate.from_price === null).length
  const dirty = Object.keys(edits).length

  async function commit() {
    const payload = Object.entries(edits)
      .map(([key, value]) => ({
        key,
        from_price: value.trim() === '' ? null : Number(value),
      }))
      .filter((entry) => entry.from_price === null || Number.isFinite(entry.from_price))
    if (payload.length === 0) return
    await save.mutateAsync(payload)
    setSaved(payload.length)
    setEdits({})
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl">Labour rates</h1>
        <p className="m-0 font-mono text-xs uppercase tracking-[0.12em] text-muted">
          {unset} of {(rates.data ?? []).length} not set
        </p>
      </div>
      <p className="mt-2 max-w-[72ch] text-sm text-muted">
        These become “Estimated labor from $X” on every product card, every line and every packet. A
        rate you have not set shows as <strong className="text-ink">“Estimated on site”</strong> —
        which is honest, so leave it blank rather than guessing. Saving writes a new version; what a
        sent packet said stays what it said.
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={onlyUnset}
            onChange={(event) => setOnlyUnset(event.target.checked)}
            className="size-4"
          />
          Only the ones still blank
        </label>
        <button
          type="button"
          disabled={dirty === 0 || save.isPending}
          onClick={() => void commit()}
          className="ml-auto min-h-11 rounded-btn bg-navy px-5 font-semibold text-white disabled:opacity-40"
        >
          {save.isPending ? 'Saving…' : `Save ${dirty || ''} change${dirty === 1 ? '' : 's'}`}
        </button>
      </div>

      {saved !== null && (
        <p role="status" className="mt-3 rounded-btn bg-blue-tint px-3.5 py-2.5 text-sm text-ink">
          Saved {saved} rate{saved === 1 ? '' : 's'}. They are live on every card immediately.
        </p>
      )}

      {rates.isPending && <p className="mt-8 text-muted">Loading…</p>}

      {rows.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-card border border-line bg-white">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                {['Used by', 'Rate key', 'Unit', 'From', 'Set by'].map((heading) => (
                  <th
                    key={heading}
                    className="p-3 font-mono text-xs uppercase tracking-[0.1em] text-muted"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((rate) => (
                <Row
                  key={rate.key}
                  rate={rate}
                  draft={edits[rate.key]}
                  onChange={(value) => setEdits((current) => ({ ...current, [rate.key]: value }))}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  )
}

function Row({
  rate,
  draft,
  onChange,
}: {
  rate: LaborRate
  draft: string | undefined
  onChange: (value: string) => void
}) {
  const value = draft ?? rate.from_price ?? ''
  return (
    <tr className="border-b border-line last:border-0">
      <td className="p-3 tabular-nums text-muted">{rate.used_by || '—'}</td>
      <td className="p-3">
        <div className="font-semibold text-ink">{rate.name ?? rate.key}</div>
        <div className="font-mono text-xs text-muted">{rate.key}</div>
      </td>
      <td className="p-3 whitespace-nowrap text-muted">{UNIT_LABEL[rate.unit] ?? rate.unit}</td>
      <td className="p-3">
        <div className="flex items-center gap-1.5">
          <span className="text-muted">$</span>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={value}
            placeholder="on site"
            aria-label={`Rate for ${rate.name ?? rate.key}`}
            onChange={(event) => onChange(event.target.value)}
            className={`min-h-10 w-28 rounded-btn border bg-white px-2 text-sm text-ink ${
              draft !== undefined ? 'border-blue' : 'border-line'
            }`}
          />
        </div>
      </td>
      <td className="p-3 text-xs text-muted">
        {rate.set_by === 'seed' ? '—' : `${rate.set_by ?? '—'} · v${rate.version}`}
      </td>
    </tr>
  )
}
